/**
 * Roads — the road-data contract for pathing and movement bonuses.
 *
 * Canonical data (public/data/buildings_*.json, read-only mirror) defines
 * roads as *buildings*: `road` (Improved Road, E1), `stone_road` (Paved
 * Road, E3), `gaul_track` (Trackway, E1). Each carries:
 *   - `is_path: true` in def.extra
 *   - `path_modifiers: { move_speed, haul_speed }` in def.extra
 *     (data-driven speed bonuses — never retuned here)
 *   - `footprint: [w, d]` in def.extra (axis-aligned; PlacedBuilding has
 *     no rotation, so road segments are axis-aligned rects)
 *
 * ROAD-DATA CONTRACT (for the map generator / future road tools):
 * A road network is a flat list of axis-aligned rects. Each rect:
 *   { x, z }       centre in world units
 *   { hw, hd }     half-extents in world units (footprint w/2, d/2)
 *   { moveBonus }  formation speed multiplier on the rect (>= 1)
 *   { haulBonus }  worker haul speed multiplier on the rect (>= 1)
 * Rects may overlap; the strongest bonus wins per point. The map
 * generator will fill this list directly (no buildings needed); the
 * gameplay path (player/AI-built roads) syncs live buildings into it via
 * `roadsFromBuildings()` below. Only `state === "active"` buildings
 * count — a road under construction gives no bonus.
 *
 * Consumers:
 * - sim/nav.ts bakes `1 / moveBonus` into flow-field traversal cost, so
 *   formations *prefer* roads when a route exists (Dijkstra).
 * - render/units.ts narrows formations to a 4-abreast marching column
 *   while the anchor is on a road and multiplies march speed by the
 *   road bonus (stacking with the marching_column formation's own
 *   `road_speed_bonus` data modifier).
 * - sim/workers.ts multiplies haul speed by `haulBonus` on roads.
 */
import type { PlacedBuilding } from "./buildings";

/** One axis-aligned road rect (the contract the map generator fills). */
export interface RoadRect {
  x: number;
  z: number;
  hw: number;
  hd: number;
  moveBonus: number;
  haulBonus: number;
}

function num(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

/**
 * Sync live road buildings into road rects. Reads only canonical
 * data fields (`is_path`, `path_modifiers`, `footprint` in def.extra).
 */
export function roadsFromBuildings(buildings: PlacedBuilding[]): RoadRect[] {
  const rects: RoadRect[] = [];
  for (const b of buildings) {
    if (b.state !== "active") continue;
    const extra = b.def.extra as Record<string, unknown>;
    if (extra["is_path"] !== true) continue;
    const fp = extra["footprint"] as unknown;
    const w = Array.isArray(fp) ? num(fp[0], 0) : 0;
    const d = Array.isArray(fp) ? num(fp[1], 0) : 0;
    if (w <= 0 || d <= 0) continue;
    const mods = (extra["path_modifiers"] ?? {}) as Record<string, unknown>;
    rects.push({
      x: b.x,
      z: b.z,
      hw: w / 2,
      hd: d / 2,
      moveBonus: Math.max(1, num(mods["move_speed"], 1)),
      haulBonus: Math.max(1, num(mods["haul_speed"], 1)),
    });
  }
  return rects;
}

/**
 * Point queries over the road rect list. Pure sim-side, render-free.
 * With no rects every query is the identity (bonus 1, onRoad false),
 * so maps without roads behave exactly as before.
 */
export class RoadNetwork {
  private rects: RoadRect[] = [];

  /** Read-only view of the active rects (debug probes). */
  list(): readonly RoadRect[] {
    return this.rects;
  }

  setRoads(rects: RoadRect[]): void {
    this.rects = rects;
  }

  get count(): number {
    return this.rects.length;
  }

  /** Strongest formation speed bonus covering (x, z); 1 when off-road. */
  moveBonusAt(x: number, z: number): number {
    let best = 1;
    for (const r of this.rects) {
      if (
        Math.abs(x - r.x) <= r.hw &&
        Math.abs(z - r.z) <= r.hd &&
        r.moveBonus > best
      ) {
        best = r.moveBonus;
      }
    }
    return best;
  }

  /** Strongest haul speed bonus covering (x, z); 1 when off-road. */
  haulBonusAt(x: number, z: number): number {
    let best = 1;
    for (const r of this.rects) {
      if (
        Math.abs(x - r.x) <= r.hw &&
        Math.abs(z - r.z) <= r.hd &&
        r.haulBonus > best
      ) {
        best = r.haulBonus;
      }
    }
    return best;
  }

  onRoad(x: number, z: number): boolean {
    return this.moveBonusAt(x, z) > 1;
  }
}
